/**
 * Milestone tracking catalog — defines allowed features and event types.
 * Frontend only reports what happened + optional product_id.
 * All classification (demo/own, eligibility) is derived server-side via JOINs.
 */

// EVENT_TYPES — what kind of interaction
export const EVENT_TYPES = {
  PAGE_VISIT: 'page_visit',       // auto-tracked route hit
  CLICK: 'click',                 // high-intent button press
  FEATURE_USED: 'feature_used',   // meaningful outcome completed
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

// FEATURES — which product area
export const FEATURES = {
  // AI Listing Generator (5-step wizard)
  AI_LISTING_GENERATOR: 'ai_listing_generator',
  AI_LISTING_STEP_1: 'ai_listing_step_1',
  AI_LISTING_STEP_2: 'ai_listing_step_2',
  AI_LISTING_STEP_3: 'ai_listing_step_3',
  AI_LISTING_STEP_4: 'ai_listing_step_4',
  AI_LISTING_STEP_5: 'ai_listing_step_5',

  // Image Generator
  IMAGE_GENERATOR: 'image_generator',
  IMAGE_GENERATOR_STANDALONE: 'image_generator_standalone',
  IMAGE_GENERATOR_PRODUCT: 'image_generator_product',

  // Listing Optimization
  LISTING_OPTIMIZATION: 'listing_optimization',
  LISTING_OPTIMIZATION_AUDIT: 'listing_opt_audit',
  LISTING_OPTIMIZATION_DONE: 'listing_opt_done',

  // URL Analyzer
  URL_ANALYZER: 'url_analyzer',

  // Onboarding/Marketplace
  MARKETPLACE_CONNECT: 'marketplace_connect',

  // Dashboard
  DASHBOARD: 'dashboard',
} as const;

export type Feature = typeof FEATURES[keyof typeof FEATURES];

// Route-to-feature mapping for automatic page_visit tracking
export const ROUTE_FEATURE_MAP: Record<string, Feature> = {
  '/ai-listing-generator': FEATURES.AI_LISTING_GENERATOR,
  '/image-generator': FEATURES.IMAGE_GENERATOR,
  '/product-listing': FEATURES.LISTING_OPTIMIZATION,
  '/url-listing-analyzer': FEATURES.URL_ANALYZER,
  '/dashboard': FEATURES.DASHBOARD,
};

// Validation: allowed feature + event_type combinations
const ALLOWED_COMBINATIONS = new Set<string>([
  // Page visits (auto-tracked)
  `${FEATURES.AI_LISTING_GENERATOR}:${EVENT_TYPES.PAGE_VISIT}`,
  `${FEATURES.IMAGE_GENERATOR}:${EVENT_TYPES.PAGE_VISIT}`,
  `${FEATURES.LISTING_OPTIMIZATION}:${EVENT_TYPES.PAGE_VISIT}`,
  `${FEATURES.URL_ANALYZER}:${EVENT_TYPES.PAGE_VISIT}`,
  `${FEATURES.DASHBOARD}:${EVENT_TYPES.PAGE_VISIT}`,

  // AI Listing step completions
  `${FEATURES.AI_LISTING_STEP_1}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.AI_LISTING_STEP_2}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.AI_LISTING_STEP_3}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.AI_LISTING_STEP_4}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.AI_LISTING_STEP_5}:${EVENT_TYPES.FEATURE_USED}`,

  // Image generator
  `${FEATURES.IMAGE_GENERATOR_STANDALONE}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.IMAGE_GENERATOR_PRODUCT}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.IMAGE_GENERATOR}:${EVENT_TYPES.FEATURE_USED}`,

  // Listing optimization
  `${FEATURES.LISTING_OPTIMIZATION_AUDIT}:${EVENT_TYPES.FEATURE_USED}`,
  `${FEATURES.LISTING_OPTIMIZATION_DONE}:${EVENT_TYPES.FEATURE_USED}`,

  // URL analyzer
  `${FEATURES.URL_ANALYZER}:${EVENT_TYPES.FEATURE_USED}`,

  // Marketplace
  `${FEATURES.MARKETPLACE_CONNECT}:${EVENT_TYPES.CLICK}`,
  `${FEATURES.MARKETPLACE_CONNECT}:${EVENT_TYPES.FEATURE_USED}`,
]);

export function isValidCombination(feature: string, eventType: string): boolean {
  return ALLOWED_COMBINATIONS.has(`${feature}:${eventType}`);
}
